import { useCallback, useEffect, useState } from 'react';
import {
  createPrintJob,
  discoverPrinters,
  fetchPrintJobs,
  getAvailableTransports,
  sendToPrinter,
  updatePrintJobStatus,
} from '../services/printing';
import type {
  DiscoveredPrinter,
  LabelTemplate,
  PrintAssetSelection,
  PrintJob,
  PrintJobStatus,
  PrintTransportType,
} from '../types/printing';

export function usePrintJobs(options?: { limit?: number; status?: PrintJobStatus }) {
  const [jobs, setJobs] = useState<PrintJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Destructure to primitives so the dependency array is stable
  const limit = options?.limit;
  const status = options?.status;

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchPrintJobs({ limit, status });
      setJobs(data);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch print jobs'));
    } finally {
      setLoading(false);
    }
  }, [limit, status]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { jobs, loading, error, refresh };
}

export function usePrinter() {
  const [printers, setPrinters] = useState<DiscoveredPrinter[]>([]);
  const [selectedPrinter, setSelectedPrinter] = useState<DiscoveredPrinter | null>(null);
  const [availableTransports] = useState<PrintTransportType[]>(getAvailableTransports());
  const [printing, setPrinting] = useState(false);
  const [lastError, setLastError] = useState<string | null>(null);

  const scanForPrinters = useCallback(async () => {
    try {
      const found = await discoverPrinters();
      setPrinters(found);
      if (found.length > 0 && !selectedPrinter) {
        setSelectedPrinter(found[0]);
      }
    } catch (err) {
      console.error('Printer discovery failed:', err);
    }
  }, [selectedPrinter]);

  const executePrintJob = useCallback(
    async (params: {
      selections: PrintAssetSelection[];
      template: LabelTemplate;
      transport: PrintTransportType;
      printerAddress?: string;
      printedBy?: string;
      companyName?: string;
    }): Promise<PrintJob | null> => {
      setPrinting(true);
      setLastError(null);

      try {
        // Create job in DB
        const job = await createPrintJob({
          ...params,
          printerName: selectedPrinter?.name,
          printerAddress: params.printerAddress || selectedPrinter?.address,
        });

        // Update status to printing
        await updatePrintJobStatus(job.id, 'printing');

        // Send to printer
        try {
          await sendToPrinter(job.generated_zpl || '', params.transport, {
            address: params.printerAddress || selectedPrinter?.address,
          });

          // Resolve printer name — use selected printer, transport type, or network address
          const resolvedPrinterName =
            selectedPrinter?.name ||
            (params.transport === 'clipboard'
              ? 'Clipboard'
              : params.transport === 'network'
                ? `Network (${params.printerAddress})`
                : params.transport === 'usb'
                  ? 'USB Printer'
                  : 'Bluetooth Printer');

          const resolvedPrinterAddress =
            params.printerAddress || selectedPrinter?.address || undefined;

          await updatePrintJobStatus(job.id, 'completed', {
            labelsPrinted: job.total_labels,
            printerName: resolvedPrinterName,
            printerAddress: resolvedPrinterAddress,
          });

          return { ...job, status: 'completed' as PrintJobStatus };
        } catch (printErr) {
          const errorMsg = printErr instanceof Error ? printErr.message : 'Print failed';
          await updatePrintJobStatus(job.id, 'failed', {
            errorMessage: errorMsg,
          });
          setLastError(errorMsg);
          return { ...job, status: 'failed' as PrintJobStatus, error_message: errorMsg };
        }
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : 'Failed to create print job';
        setLastError(errorMsg);
        return null;
      } finally {
        setPrinting(false);
      }
    },
    [selectedPrinter]
  );

  return {
    printers,
    selectedPrinter,
    setSelectedPrinter,
    availableTransports,
    printing,
    lastError,
    scanForPrinters,
    executePrintJob,
  };
}
